/**
 * CameraRail — the desktop vertical scrolling list of cameras and the mobile
 * camera panel list.
 *
 * Reads cameras from LayoutContext. Each row shows the live video track
 * (when online) or a fallback. Tapping a row updates `selection` and asks
 * map-capable shells to put that camera on the shared canvas.
 */

import { Star } from 'lucide-react'
import {
  registerPrimitive,
  useShellSurface,
  type PrimitiveProps,
} from '../../layout'
import {
  CameraVideo,
  useCameraSelection,
  useCameras,
} from '../../destination'

interface CameraRailConfig {
  /** Sort mode. Default "position" — by km along the route, falling back to name. */
  sortBy?: 'position' | 'name'
}

function FeaturedStar() {
  return (
    <span className="lv-thumb-featured" role="img" aria-label="Producer's featured camera">
      <Star size={11} fill="currentColor" aria-hidden="true" />
    </span>
  )
}

function CameraThumb({
  identity,
  featured,
}: {
  identity: string
  featured: boolean
}) {
  return (
    <div className="lv-thumb">
      <CameraVideo identity={identity} alt="" />
      <span className="lv-thumb-live" role="img" aria-label="Live">
        <span className="lv-live-dot" />
      </span>
      {featured && <FeaturedStar />}
    </div>
  )
}

function RailSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="lv-rail" aria-busy="true" aria-label="Cameras coming online">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="lv-cam-skel">
          <div className="lv-skel lv-skel-thumb" />
          <div className="lv-skel-text">
            <div className="lv-skel lv-skel-line-1" />
            <div className="lv-skel lv-skel-line-2" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function CameraRail({ config }: PrimitiveProps) {
  const cameras = useCameras()
  const selection = useCameraSelection()
  const { mode: surfaceMode, requestMode } = useShellSurface()
  const cfg = config as CameraRailConfig
  const sortBy = cfg.sortBy ?? 'position'

  // Only surface online cameras in the rail. Off-air event = skeleton.
  const live = cameras.filter((c) => c.online)

  const sorted = [...live].sort((a, b) => {
    if (sortBy === 'position') {
      const ak = a.positionKm ?? Number.POSITIVE_INFINITY
      const bk = b.positionKm ?? Number.POSITIVE_INFINITY
      if (ak !== bk) return ak - bk
    }
    return a.name.localeCompare(b.name)
  })

  if (sorted.length === 0) {
    return (
      <>
        <div className="lv-rail-head">
          <div className="lv-rail-head-left">
            <span className="lv-rail-title">Cameras</span>
            <span className="lv-skel lv-rail-count-skel" aria-label="Loading camera list" />
          </div>
        </div>
        <RailSkeleton count={cameras.length > 0 ? Math.min(cameras.length, 5) : 5} />
      </>
    )
  }

  return (
    <>
      <div className="lv-rail-head">
        <div className="lv-rail-head-left">
          <span className="lv-rail-title">Cameras</span>
          <span className="lv-rail-count-badge">{sorted.length} live</span>
        </div>
      </div>

      <div className="lv-rail">
        {sorted.map((cam) => {
          const isActive =
            selection.selectedIdentity === cam.identity
          const isFeatured =
            selection.producerFeaturedIdentity === cam.identity
          return (
            <button
              key={cam.identity}
              type="button"
              className={`lv-cam${isActive ? ' lv-cam-active' : ''}${isFeatured ? ' lv-cam-featured' : ''}`}
              aria-pressed={isActive}
              onClick={() => {
                selection.select(cam.identity)
                if (surfaceMode !== null) requestMode('camera')
              }}
            >
              <CameraThumb identity={cam.identity} featured={isFeatured} />
              <div className="lv-cam-text">
                <span className="lv-cam-name">{cam.name}</span>
              </div>
            </button>
          )
        })}
      </div>
    </>
  )
}

registerPrimitive('CameraRail', CameraRail)
